import { LOCATION_FILTERS as T } from '@locales/id/locations';
import type { Province, Regency } from '@lib/api';
import { shortProvince, shortRegency } from '@lib/format';
import type { FilterChip, ListFilter } from '@ui';
import { RegionPanel } from './LocationFilterPanels';

interface RegionFilterInput {
  provinces: Province[] | null;
  regencies: Regency[] | null;
  regenciesFailed: boolean;
  provinceCode: string;
  regencyCode: string;
  /** a province account: its province is fixed (no chip, no list of provinces) */
  locked: boolean;
  onProvince: (code: string) => void;
  onRegency: (code: string) => void;
}

/**
 * Wilayah (province, then kabupaten/kota) as one menu of a ListToolbar, with its chips: the same filter on every
 * list that narrows by region without the kecamatan/desa levels (Pesanan, Warga, Pendaftaran Hub).
 */
export function regionFilter(r: RegionFilterInput): { filter: ListFilter; chips: FilterChip[] } {
  const provinceName = r.provinceCode && !r.locked ? shortProvince(r.provinces?.find((p) => p.code === r.provinceCode)?.name ?? r.provinceCode) : null;
  const regencyName = r.regencyCode ? shortRegency(r.regencies?.find((x) => x.code === r.regencyCode)?.name ?? r.regencyCode) : null;
  const text = [provinceName, regencyName].filter(Boolean).join(' · ');
  return {
    filter: {
      key: 'region',
      label: T.region,
      text: text ? `${T.region}: ${text}` : T.region,
      active: !!text,
      wide: true,
      panel: (close) => (
        <RegionPanel
          provinces={r.provinces}
          regencies={r.regencies}
          regenciesFailed={r.regenciesFailed}
          provinceCode={r.provinceCode}
          regencyCode={r.regencyCode}
          locked={r.locked}
          onProvince={r.onProvince}
          onRegency={(code) => {
            r.onRegency(code);
            if (code) close?.();
          }}
        />
      ),
    },
    chips: [
      ...(provinceName ? [{ key: 'province', label: provinceName, name: T.province, icon: 'map' as const, onRemove: () => r.onProvince('') }] : []),
      ...(regencyName ? [{ key: 'regency', label: regencyName, name: T.regency, icon: 'pin' as const, onRemove: () => r.onRegency('') }] : []),
    ],
  };
}
